import React, { ReactNode, useMemo } from "react";

import { useIntl } from "react-intl";

import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import styles from "./BaseStylesEditor.module.scss";

interface Props {
  children: ReactNode;
  description?: ReactNode;
  onReset?: () => void;
  pinLegendType?: PinLegendType;
  resetIsDisabled?: boolean;
}

const BaseStyleEditor: React.FC<Props> = ({
  children,
  description,
  onReset,
  pinLegendType,
  resetIsDisabled,
}) => {
  const intl = useIntl();

  const resetButtonText = useMemo(
    () =>
      intl.formatMessage({
        id: "map.colorization.createEditSidebar.resetAll",
        defaultMessage: "Reset all",
        description: "Reset all button in Create Pin Legend drawer on the Map page",
      }),
    [intl]
  );

  return (
    <div>
      <div className={styles.header}>
        <span className={styles.title}>
          {intl.formatMessage({
            id: "map.colorization.createEditSidebar.styleEditor",
            defaultMessage: "Review styles",
            description:
              "Title of the Styles Editor section in Create Pin Legend drawer on the Map page",
          })}
        </span>
        {pinLegendType &&
          (resetIsDisabled ? (
            <Button disabled type="filled">
              {resetButtonText}
            </Button>
          ) : (
            <Popconfirm
              cancelText={intl.formatMessage({
                id: "map.colorization.createEditSidebar.resetAll.cancel",
                defaultMessage: "Cancel",
                description: "Cancel button in Create Pin Legend drawer on the Map page",
              })}
              description={
                pinLegendType === PinLegendType.SHAPE
                  ? intl.formatMessage({
                      id: "map.colorization.createEditSidebar.resetAll.confirmation.label.shape",
                      defaultMessage: "Are you sure you want to reset all shapes to be “Square”?",
                      description:
                        "Confirmation message for Reset all button in Create Pin Legend drawer on the Map page",
                    })
                  : intl.formatMessage({
                      id: "map.colorization.createEditSidebar.resetAll.confirmation.label.color",
                      defaultMessage: "Are you sure you want to reset all colors to be “Gray”?",
                      description:
                        "Confirmation message for Reset all button in Create Pin Legend drawer on the Map page",
                    })
              }
              okText={intl.formatMessage({
                id: "map.colorization.createEditSidebar.resetAll.confirm",
                defaultMessage: "Reset",
                description: "Reset button in Create Pin Legend drawer on the Map page",
              })}
              onConfirm={onReset}
              placement="topLeft"
            >
              <Button type="filled">{resetButtonText}</Button>
            </Popconfirm>
          ))}
      </div>

      <div className={styles.container}>
        {description && <div className={styles.description}>{description}</div>}
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};

export default BaseStyleEditor;
